import { useState, useRef, useEffect, type CSSProperties, type KeyboardEvent } from "react";
import type { TableColumn } from "../../lib/v3-columns";
import { validateField } from "../../lib/v3-columns";
import type { AvailableTransition } from "../../../lib/types";

/**
 * Sprint 12b — when the column being edited backs a workflow state, the parent
 * passes `workflowState` so the enum dropdown can grey out states the server
 * says aren't directly reachable (mirrors the StageStepper). Undefined for every
 * non-workflow enum, so ordinary enums render all choices unchanged.
 */
interface WorkflowEditState {
  field: string;
  current: string;
  transitions: AvailableTransition[];
}

interface CellEditorProps {
  value: unknown;
  column: TableColumn;
  onSave: (value: unknown) => void;
  onCancel: () => void;
  onTab: (shift: boolean) => void;
  workflowState?: WorkflowEditState;
}

export function CellEditor({ value, column, onSave, onCancel, onTab, workflowState }: CellEditorProps) {
  const [draft, setDraft] = useState(value === null || value === undefined ? "" : String(value));
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    if (inputRef.current instanceof HTMLInputElement) {
      inputRef.current.select();
    }
  }, []);

  function handleChange(next: string) {
    setDraft(next);
    if (error) {
      const err = validateField(next, column);
      if (!err) setError(null);
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      save();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
    if (e.key === "Tab") {
      e.preventDefault();
      if (trySave()) {
        onTab(e.shiftKey);
      }
    }
  }

  function trySave(): boolean {
    const err = validateField(draft, column);
    if (err) {
      setError(err);
      return false;
    }
    const parsed = parseValue(draft, column);
    onSave(parsed);
    return true;
  }

  function save() {
    trySave();
  }

  function handleBlur() {
    const err = validateField(draft, column);
    if (err) {
      setError(err);
      return;
    }
    save();
  }

  const cellStyle: CSSProperties = {
    padding: 10,
    textAlign: column.align,
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    padding: "2px 4px",
    fontSize: 13,
    fontFamily: "var(--font-sans)",
    border: "none",
    borderBottom: error
      ? "1.5px solid var(--color-border-danger)"
      : "1.5px dashed var(--kuvio-cell-edit-border)",
    background: "transparent",
    outline: "none",
    color: "var(--color-text-primary)",
    textAlign: column.align,
    ...(column.type === "number" || column.type === "currency"
      ? { fontVariantNumeric: "tabular-nums" as const }
      : {}),
  };

  const errorStyle: CSSProperties = {
    fontSize: 11,
    color: "var(--color-text-danger)",
    marginTop: 2,
    lineHeight: 1.3,
  };

  if (column.type === "enum" && column.choices) {
    // Sprint 12b — for a workflow state field, grey out choices that aren't a
    // directly-declared transition from the current state. The current value and
    // reachable states stay selectable; everything else is disabled + flagged.
    const wf = workflowState && column.field === workflowState.field ? workflowState : null;
    const reachable = wf ? new Set(wf.transitions.map((t) => t.to)) : null;
    return (
      <td style={cellStyle} onClick={(e) => e.stopPropagation()}>
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            onSave(e.target.value);
          }}
          onBlur={() => onSave(draft)}
          onKeyDown={handleKeyDown}
          style={{
            ...inputStyle,
            borderBottom: "1.5px dashed var(--kuvio-cell-edit-border)",
            cursor: "pointer",
          }}
        >
          <option value="">—</option>
          {column.choices.map((c) => {
            const blocked = !!reachable && c !== wf!.current && !reachable.has(c);
            return (
              <option
                key={c}
                value={c}
                disabled={blocked}
                title={blocked ? `No direct transition from ${wf!.current}` : undefined}
              >
                {blocked ? `${c} [?]` : c}
              </option>
            );
          })}
        </select>
      </td>
    );
  }

  if (column.type === "date" || column.type === "datetime") {
    const inputType = column.type === "datetime" ? "datetime-local" : "date";
    const dateVal = draft && !isNaN(Date.parse(draft))
      ? column.type === "datetime"
        ? new Date(draft).toISOString().slice(0, 16)
        : new Date(draft).toISOString().slice(0, 10)
      : draft;

    return (
      <td style={cellStyle} onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={inputType}
          value={dateVal}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={inputStyle}
        />
        {error && <div style={errorStyle}>{error}</div>}
      </td>
    );
  }

  return (
    <td style={cellStyle} onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type={column.type === "number" || column.type === "currency" ? "number" : "text"}
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={inputStyle}
      />
      {error && <div style={errorStyle}>{error}</div>}
    </td>
  );
}

function parseValue(draft: string, column: TableColumn): unknown {
  if (draft === "") return null;
  if (column.type === "number" || column.type === "currency") {
    const n = parseFloat(draft.replace(/[,$]/g, ""));
    return isNaN(n) ? draft : n;
  }
  return draft;
}
